import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Table,
  Badge,
} from 'react-bootstrap';
import {
  FaSearch,
  FaHistory,
  FaArrowLeft,
  FaFileExcel,
  FaUser,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import consultaHistorialHuespedApi from '../../../services/api/consultaHistorialHuespedApi';

function formatDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'object' && d.msg ? d.msg : JSON.stringify(d)))
      .join(' ');
  }
  return String(detail);
}

const estadoBadge = (estado) => {
  const e = (estado || '').toLowerCase();
  if (e === 'ocupada') return 'success';
  if (e === 'reservada') return 'warning';
  if (e === 'finalizada') return 'secondary';
  return 'info';
};

const ConsultaHistorialHuesped = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(
    localStorage.getItem('selectedProject') || 'null'
  );

  const [dni, setDni] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);

  const consultar = useCallback(
    async (e) => {
      e?.preventDefault?.();
      setError('');
      setResultado(null);

      const doc = dni.replace(/\s/g, '').trim();
      if (!doc) {
        setError('Ingrese el DNI del huésped.');
        return;
      }
      if (!selectedProject?.id) {
        setError('Seleccione un proyecto en el inicio de sesión.');
        return;
      }

      setLoading(true);
      try {
        const res = await consultaHistorialHuespedApi.consultarPorDni(
          doc,
          selectedProject.id
        );
        if (res?.success && res?.data) {
          setResultado(res.data);
        } else {
          setError(res?.message || 'Respuesta inesperada del servidor.');
        }
      } catch (err) {
        const d =
          err.response?.data?.detail ??
          err.response?.data?.message ??
          err.message;
        setError(formatDetail(d) || 'No se pudo completar la consulta.');
      } finally {
        setLoading(false);
      }
    },
    [dni, selectedProject?.id]
  );

  const exportarExcel = () => {
    if (!resultado?.estadias?.length) return;
    const rows = resultado.estadias.map((e, idx) => ({
      '#': idx + 1,
      DNI: resultado.trabajador?.dni ?? '',
      Trabajador: resultado.trabajador?.nombre_completo ?? '',
      Zona: e.zona ?? '',
      Módulo: e.modulo ?? '',
      Habitación: e.habitacion ?? '',
      Cama: e.cama ?? '',
      'Fecha reserva': e.fecha_reserva_fmt ?? '—',
      'Check-in': e.fecha_check_in_fmt ?? '—',
      'Check-out': e.fecha_check_out_fmt ?? '—',
      'Días estadía': e.dias_estadia ?? '',
      Estado: e.estado_asignacion ?? '',
      Activo: e.activo ? 'Sí' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historial');
    const nombre = `historial_huesped_${resultado.trabajador?.dni || 'consulta'}.xlsx`;
    XLSX.writeFile(wb, nombre);
  };

  const { trabajador, estadias = [], resumen } = resultado || {};

  return (
    <>
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.75rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-alert-info {
            border-radius: 12px;
            border: none;
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            color: #1e40af;
          }
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.85rem !important;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                <FaHistory size={36} />
                <div>
                  <h1 className="dh-main-title">Historial de huésped</h1>
                  <p className="dh-main-subtitle">
                    Consulta check-in, check-out y habitación por DNI en el proyecto
                    {selectedProject?.nombre
                      ? `: ${selectedProject.nombre}`
                      : ''}
                  </p>
                </div>
              </div>
              <Button
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate('/home')}
              >
                <FaArrowLeft className="me-2" /> Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">
              <FaSearch className="me-2" /> Buscar huésped
            </Card.Header>
            <Card.Body className="p-4">
              <Alert variant="info" className="dh-alert dh-alert-info">
                Ingrese el DNI del trabajador registrado en hotelería del proyecto
                actual. Se listan todas las reservas y estadías (activas y
                finalizadas). Para todos los DNI use{' '}
                <Link to="/reportes/hoteleria/historial-huesped">
                  Reportes → Historial de huéspedes
                </Link>
                .
              </Alert>
              <Form onSubmit={consultar}>
                <Row className="align-items-end g-3">
                  <Col md={4}>
                    <Form.Label className="dh-form-label">DNI</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={dni}
                      onChange={(ev) => setDni(ev.target.value)}
                      placeholder="Ej. 12345678"
                      maxLength={20}
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      type="submit"
                      className="btn-dh-primary d-flex align-items-center"
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : (
                        <FaSearch className="me-2" />
                      )}
                      Consultar
                    </Button>
                  </Col>
                </Row>
              </Form>
              {error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {trabajador && (
            <>
              <Card className="dh-card mb-4">
                <Card.Body className="p-4">
                  <Row>
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FaUser className="text-primary" />
                        <strong>{trabajador.nombre_completo}</strong>
                      </div>
                      <div className="text-muted">
                        DNI: <strong>{trabajador.dni}</strong>
                      </div>
                    </Col>
                    {resumen && (
                      <Col md={6}>
                        <div className="small text-muted">
                          Registros: <strong>{resumen.total_registros}</strong>
                          {' · '}
                          Con check-in:{' '}
                          <strong>{resumen.total_con_check_in}</strong>
                          {' · '}
                          Con check-out:{' '}
                          <strong>{resumen.total_con_check_out}</strong>
                          {' · '}
                          Días hospedado (suma):{' '}
                          <strong>{resumen.total_dias_hospedado}</strong>
                        </div>
                        {resumen.tiene_estadia_activa && (
                          <Badge bg="success" className="mt-2">
                            Actualmente con check-in activo
                          </Badge>
                        )}
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>

              <Card className="dh-card">
                <Card.Header className="dh-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Detalle de estadías</span>
                  {estadias.length > 0 && (
                    <Button
                      size="sm"
                      variant="light"
                      className="d-flex align-items-center"
                      onClick={exportarExcel}
                    >
                      <FaFileExcel className="me-1 text-success" /> Excel
                    </Button>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {estadias.length === 0 ? (
                    <Alert variant="warning" className="m-4">
                      No hay registros de hospedaje para este trabajador en el
                      proyecto.
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table striped hover className="mb-0 dh-enhanced-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Zona</th>
                            <th>Módulo</th>
                            <th>Habitación</th>
                            <th>Cama</th>
                            <th>Reserva</th>
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Días</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {estadias.map((e, idx) => (
                            <tr key={e.asignacion_id ?? idx}>
                              <td>{idx + 1}</td>
                              <td>{e.zona ?? '—'}</td>
                              <td>{e.modulo ?? '—'}</td>
                              <td>{e.habitacion ?? '—'}</td>
                              <td>{e.cama ?? '—'}</td>
                              <td>{e.fecha_reserva_fmt ?? '—'}</td>
                              <td>{e.fecha_check_in_fmt ?? '—'}</td>
                              <td>{e.fecha_check_out_fmt ?? '—'}</td>
                              <td>
                                {e.dias_estadia != null ? e.dias_estadia : '—'}
                              </td>
                              <td>
                                <Badge bg={estadoBadge(e.estado_asignacion)}>
                                  {e.estado_asignacion ?? '—'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default ConsultaHistorialHuesped;
